// KidColoringCanvas — in-app touch coloring of an AI-generated
// line-art page from kid-color-craft.
//
// Two-layer canvas:
//   • background (fillCanvasRef) — kid's flood-fills + brush strokes
//   • foreground (lineCanvasRef) — line-art PNG, drawn on top so
//     black outlines stay crisp regardless of what the kid does
// Final saved PNG composites both layers.
//
// Tools:
//   • paint bucket (default) — BFS flood-fill stopping at the dark
//     pixels of the LINE-ART foreground. Lets the kid tap any
//     enclosed region and fill it with the active color.
//   • brush — freehand stroke at fixed thickness; for places the
//     flood-fill can't reach (open gaps, fine highlights).
//   • undo (last 8 actions) — ImageData snapshots.
//   • save — composites both layers → uploads PNG to
//     kid-coloring-pages bucket (path: colored/<uuid>.png) → patches
//     kid_coloring_pages.colored_url. Then iOS share sheet via
//     Capacitor Share plugin (parent saves to Photos / AirPrints).
//
// Performance: flood-fill runs on a 512x512 downsampled canvas
// (~4x faster than 1024), then upscaled. Felt instant on iPhone 12.
//
// EMOJI-IN-UI EXCEPTION: kids-mode opt-in.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { COLOR, FONT } from "../../lib/tokens";

// Kid-friendly palette. Order tuned so the colors feel like a
// crayon box: warms left, cools right, neutrals last.
const PALETTE = [
  "#E84A5F", // red
  "#FF8A3D", // orange
  "#FFD23F", // yellow
  "#52B788", // green
  "#5DA9E9", // sky
  "#3D6BB8", // blue
  "#9B5DE5", // purple
  "#F4B6C2", // pink
  "#8B5E3C", // brown
  "#F2D5B7", // peach (skin tone)
  "#1A1A1A", // black
  "#FFFFFF", // white (eraser-shaped fill)
];

const CANVAS_SIZE = 1024;
const FILL_SIZE   = 512; // flood-fill works at half-resolution
const BRUSH_PX    = 12;
const UNDO_CAP    = 8;

export function KidColoringCanvas({
  kidUserId,
  coloringPageId,
  lineArtURL,
  coloredURL,        // optional — previous saved composite for resume
  petName,
  comboName,
  onClose,
}) {
  const lineCanvasRef = useRef(null);   // top: line-art PNG (immutable)
  const fillCanvasRef = useRef(null);   // bottom: kid's fills
  const lineImageDataRef = useRef(null); // cached pixel data for fill bounds
  const [tool, setTool] = useState("bucket"); // "bucket" | "brush" | "eraser"
  const [color, setColor] = useState(PALETTE[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const undoStackRef = useRef([]);

  // ── 1. Load the line-art onto the foreground canvas ─────────────
  // The line-art PNGs from gpt-image-2 ship with a SOLID WHITE
  // background and black outlines. Rendered as-is over the fill
  // layer, the white occludes everything the kid colors below it —
  // bug "coloring doesn't color." Preprocess at load time: walk the
  // pixel buffer, convert near-white pixels to alpha=0 so only the
  // dark outlines remain. Result: a transparent line-art layer the
  // fill shows through.
  //
  // If a coloredURL exists (resume path), paint it onto the fill
  // layer so the kid keeps coloring where they left off.
  useEffect(() => {
    if (!lineArtURL) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const lineCanvas = lineCanvasRef.current;
      const fillCanvas = fillCanvasRef.current;
      if (!lineCanvas || !fillCanvas) return;
      lineCanvas.width  = CANVAS_SIZE;
      lineCanvas.height = CANVAS_SIZE;
      fillCanvas.width  = CANVAS_SIZE;
      fillCanvas.height = CANVAS_SIZE;
      const lctx = lineCanvas.getContext("2d");
      const fctx = fillCanvas.getContext("2d");
      fctx.fillStyle = "#FFFFFF";
      fctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      // Draw into the line canvas + walk pixel buffer to mask
      // white → transparent. Threshold of 235 catches the JPEG-ish
      // off-white (gpt-image-2 PNGs aren't pure 255-white at edges).
      lctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      lctx.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE);
      try {
        const imgData = lctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE);
        const d = imgData.data;
        const WHITE_THRESHOLD = 235;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i] > WHITE_THRESHOLD && d[i + 1] > WHITE_THRESHOLD && d[i + 2] > WHITE_THRESHOLD) {
            d[i + 3] = 0; // alpha → 0
          }
        }
        lctx.putImageData(imgData, 0, 0);
      } catch (e) {
        // CORS-tainted canvas (shouldn't happen — bucket sends
        // Access-Control-Allow-Origin: *, but defensive). Fall
        // through with the white-backed version; fills won't be
        // visible but at least the page renders.
        console.warn("[coloring-canvas] white-mask skipped:", e?.message || e);
      }
      // Cache the FILL_SIZE-downsampled line-art data for flood
      // fill outline detection. Same alpha-masked pixels — fills
      // stop at any non-transparent (i.e. outline) pixel. Wrap in
      // try/catch so a CORS-tainted canvas (rare but happens on
      // iOS WebKit caches) leaves the brush path working even if
      // fill stays broken. Draw DIRECTLY from the source Image
      // (not from the line canvas) so we don't propagate any
      // taint from prior drawImage operations.
      try {
        const scratch = document.createElement("canvas");
        scratch.width  = FILL_SIZE;
        scratch.height = FILL_SIZE;
        const sctx = scratch.getContext("2d");
        sctx.drawImage(img, 0, 0, FILL_SIZE, FILL_SIZE);
        lineImageDataRef.current = sctx.getImageData(0, 0, FILL_SIZE, FILL_SIZE);
      } catch (e) {
        console.warn("[coloring-canvas] outline data unreadable (CORS?); fill disabled:", e?.message || e);
        lineImageDataRef.current = null;
      }

      if (coloredURL) {
        const resumeImg = new Image();
        resumeImg.crossOrigin = "anonymous";
        resumeImg.onload = () => {
          fctx.drawImage(resumeImg, 0, 0, CANVAS_SIZE, CANVAS_SIZE);
        };
        resumeImg.onerror = () => { /* non-fatal, just start fresh */ };
        resumeImg.src = coloredURL;
      }
    };
    img.onerror = () => setError("Couldn't load the picture");
    // Cache-bust to force iOS WebKit to fetch fresh and honor the
    // crossOrigin=anonymous request. Without this, an earlier
    // (CORS-less) load of the same URL would have cached the image
    // without ACAO metadata; subsequent loads pull the tainted
    // cached version, and getImageData throws SecurityError →
    // flood-fill silently no-ops. Brush works either way (it
    // doesn't read pixel data).
    img.src = lineArtURL + (lineArtURL.includes("?") ? "&" : "?") + "kbnocache=" + Date.now();
  }, [lineArtURL, coloredURL]);

  // ── 2. Tool: paint bucket (flood fill) ──────────────────────────
  // BFS at FILL_SIZE resolution. A pixel "blocks" the fill if its
  // luminance is below the threshold (black outlines of the line art)
  // OR if it's already been filled with the SAME color (no-op).
  const floodFillAt = useCallback((x, y, hex) => {
    const fillCanvas = fillCanvasRef.current;
    const lineData   = lineImageDataRef.current;
    if (!fillCanvas || !lineData) return;
    const fctx = fillCanvas.getContext("2d");
    // Read existing fill at FILL_SIZE.
    const scratch = document.createElement("canvas");
    scratch.width  = FILL_SIZE;
    scratch.height = FILL_SIZE;
    const sctx = scratch.getContext("2d");
    sctx.drawImage(fillCanvas, 0, 0, FILL_SIZE, FILL_SIZE);
    const fillData = sctx.getImageData(0, 0, FILL_SIZE, FILL_SIZE);

    const targetR = fillData.data[(y * FILL_SIZE + x) * 4];
    const targetG = fillData.data[(y * FILL_SIZE + x) * 4 + 1];
    const targetB = fillData.data[(y * FILL_SIZE + x) * 4 + 2];

    const [nr, ng, nb] = hexToRgb(hex);
    if (targetR === nr && targetG === ng && targetB === nb) return;

    const queue = [[x, y]];
    const seen = new Uint8Array(FILL_SIZE * FILL_SIZE);
    const isOutline = (idx) => {
      // Average channel value of line-art pixel; lower = darker = outline.
      const r = lineData.data[idx * 4];
      const g = lineData.data[idx * 4 + 1];
      const b = lineData.data[idx * 4 + 2];
      const a = lineData.data[idx * 4 + 3];
      if (a < 64) return false; // transparent → fillable
      return (r + g + b) / 3 < 90;
    };
    const matchesTarget = (idx) => {
      return fillData.data[idx * 4]     === targetR
          && fillData.data[idx * 4 + 1] === targetG
          && fillData.data[idx * 4 + 2] === targetB;
    };

    while (queue.length > 0) {
      const [cx, cy] = queue.pop();
      if (cx < 0 || cy < 0 || cx >= FILL_SIZE || cy >= FILL_SIZE) continue;
      const idx = cy * FILL_SIZE + cx;
      if (seen[idx]) continue;
      seen[idx] = 1;
      if (isOutline(idx)) continue;
      if (!matchesTarget(idx)) continue;
      fillData.data[idx * 4]     = nr;
      fillData.data[idx * 4 + 1] = ng;
      fillData.data[idx * 4 + 2] = nb;
      fillData.data[idx * 4 + 3] = 255;
      queue.push([cx + 1, cy]);
      queue.push([cx - 1, cy]);
      queue.push([cx, cy + 1]);
      queue.push([cx, cy - 1]);
    }

    sctx.putImageData(fillData, 0, 0);
    // Snapshot for undo BEFORE we paint the result back.
    pushUndo();
    // Upscale back to full canvas.
    fctx.imageSmoothingEnabled = false;
    fctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    fctx.drawImage(scratch, 0, 0, CANVAS_SIZE, CANVAS_SIZE);
  }, []);

  // ── 3. Brush strokes via NATIVE listeners ──────────────────────
  // React's synthetic pointer events get swallowed by iOS WKWebView
  // gesture recognizers when attached via JSX. Attach native
  // touch/pointer listeners directly on the canvas ref with
  // passive:false so we can preventDefault and stop the OS from
  // hijacking (scroll, zoom, magnifier on long press).
  const drawingRef   = useRef(false);
  const lastPointRef = useRef(null);
  const toolRef      = useRef(tool);
  const colorRef     = useRef(color);
  useEffect(() => { toolRef.current  = tool;  }, [tool]);
  useEffect(() => { colorRef.current = color; }, [color]);

  const paintAt = useCallback((x0, y0, x1, y1) => {
    const canvas = fillCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const isEraser = toolRef.current === "eraser";
    ctx.strokeStyle = isEraser ? "#FFFFFF" : colorRef.current;
    ctx.fillStyle   = isEraser ? "#FFFFFF" : colorRef.current;
    ctx.lineWidth   = BRUSH_PX;
    ctx.lineCap     = "round";
    ctx.lineJoin    = "round";
    if (x0 === x1 && y0 === y1) {
      // Zero-length stroke renders nothing in some browsers; draw a
      // circle so the initial pointerdown leaves a visible dot.
      ctx.beginPath();
      ctx.arc(x0, y0, BRUSH_PX / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
    }
  }, []);

  useEffect(() => {
    const canvas = fillCanvasRef.current;
    if (!canvas) return;
    const ptFromEvent = (e) => {
      const rect = canvas.getBoundingClientRect();
      const src = (e.touches && e.touches[0])
        || (e.changedTouches && e.changedTouches[0])
        || e;
      const x = ((src.clientX - rect.left) / rect.width)  * CANVAS_SIZE;
      const y = ((src.clientY - rect.top)  / rect.height) * CANVAS_SIZE;
      return { x, y };
    };
    const onStart = (e) => {
      e.preventDefault();
      const { x, y } = ptFromEvent(e);
      if (toolRef.current === "bucket") {
        const fx = Math.floor((x / CANVAS_SIZE) * FILL_SIZE);
        const fy = Math.floor((y / CANVAS_SIZE) * FILL_SIZE);
        floodFillAt(fx, fy, colorRef.current);
        return;
      }
      pushUndo();
      drawingRef.current = true;
      lastPointRef.current = { x, y };
      paintAt(x, y, x, y);
    };
    const onMove = (e) => {
      if (!drawingRef.current) return;
      e.preventDefault();
      const { x, y } = ptFromEvent(e);
      const last = lastPointRef.current;
      if (last) paintAt(last.x, last.y, x, y);
      lastPointRef.current = { x, y };
    };
    const onEnd = (e) => {
      if (drawingRef.current) e.preventDefault();
      drawingRef.current = false;
      lastPointRef.current = null;
    };
    const opts = { passive: false };
    canvas.addEventListener("pointerdown",   onStart, opts);
    canvas.addEventListener("pointermove",   onMove,  opts);
    canvas.addEventListener("pointerup",     onEnd,   opts);
    canvas.addEventListener("pointercancel", onEnd,   opts);
    canvas.addEventListener("touchstart",    onStart, opts);
    canvas.addEventListener("touchmove",     onMove,  opts);
    canvas.addEventListener("touchend",      onEnd,   opts);
    canvas.addEventListener("touchcancel",   onEnd,   opts);
    return () => {
      canvas.removeEventListener("pointerdown",   onStart);
      canvas.removeEventListener("pointermove",   onMove);
      canvas.removeEventListener("pointerup",     onEnd);
      canvas.removeEventListener("pointercancel", onEnd);
      canvas.removeEventListener("touchstart",    onStart);
      canvas.removeEventListener("touchmove",     onMove);
      canvas.removeEventListener("touchend",      onEnd);
      canvas.removeEventListener("touchcancel",   onEnd);
    };
  }, [paintAt, floodFillAt]);

  // ── 4. Undo stack ────────────────────────────────────────────────
  const pushUndo = () => {
    const canvas = fillCanvasRef.current;
    if (!canvas) return;
    try {
      const ctx = canvas.getContext("2d");
      const snap = ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      const stack = undoStackRef.current;
      stack.push(snap);
      if (stack.length > UNDO_CAP) stack.shift();
    } catch {
      // SecurityError when image isn't CORS-clean — skip silently
      // rather than break the canvas.
    }
  };
  const undo = () => {
    const stack = undoStackRef.current;
    if (stack.length === 0) return;
    const snap = stack.pop();
    const canvas = fillCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.putImageData(snap, 0, 0);
  };

  // ── 5. Save (composite both layers, upload, patch row) ───────────
  // SUCCESS UX: after the upload + DB patch, show a "saved to your
  // coloring book" toast and auto-close after a beat. The
  // ColoringBook gallery on the Pet tab realtime-refreshes via
  // useKidColoringPages, so the new colored composite appears
  // there immediately. iOS share / Photos save is deferred — the
  // @capacitor/share + filesystem plugins aren't installed yet
  // and the previous URL-share fell through to a broken download
  // anchor that did nothing useful.
  const [savedToast, setSavedToast] = useState(false);
  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const out = document.createElement("canvas");
      out.width = CANVAS_SIZE;
      out.height = CANVAS_SIZE;
      const octx = out.getContext("2d");
      // White base so the kid's fills + line art composite cleanly
      // (line art is now alpha-masked at load time so its
      // background is transparent — without this base the composite
      // PNG would have a transparent backdrop that looks weird in
      // the gallery tile preview).
      octx.fillStyle = "#FFFFFF";
      octx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      octx.drawImage(fillCanvasRef.current, 0, 0);
      octx.drawImage(lineCanvasRef.current, 0, 0);

      // JPEG, not PNG — egress discipline. The 1024px composite as
      // PNG runs 0.5–2 MB and gets served back to every family
      // viewer from a public bucket; JPEG 0.85 lands ~10x smaller.
      // The white base above guarantees no alpha is lost, and 0.85
      // keeps the line art crisp enough for gallery tiles.
      const blob = await new Promise((res) => out.toBlob(res, "image/jpeg", 0.85));
      if (!blob) throw new Error("composite blob failed");

      const path = `colored/${crypto.randomUUID()}.jpg`;
      const { error: upErr } = await supabase.storage
        .from("kid-coloring-pages")
        .upload(path, blob, {
          contentType: "image/jpeg",
          upsert: false,
          cacheControl: "public, max-age=2592000, immutable",
        });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("kid-coloring-pages").getPublicUrl(path);
      const coloredURL = pub.publicUrl;

      if (coloringPageId) {
        const { error: updErr } = await supabase
          .from("kid_coloring_pages")
          .update({ colored_url: coloredURL, colored_at: new Date().toISOString() })
          .eq("id", coloringPageId)
          .eq("kid_user_id", kidUserId);
        if (updErr) console.warn("[KidColoringCanvas] patch failed:", updErr.message);
      }

      setSavedToast(true);
      // Auto-close after the toast plays a beat so the kid sees the
      // gallery update.
      setTimeout(() => {
        setSavedToast(false);
        if (typeof onClose === "function") onClose();
      }, 1400);
    } catch (e) {
      console.error("[KidColoringCanvas] save failed:", e);
      setError(e?.message || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#FAF6EE",
        zIndex: 320,
        display: "flex",
        flexDirection: "column",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        borderBottom: `1px solid ${COLOR.border}`,
      }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            fontSize: 28,
            cursor: "pointer",
            padding: 4,
          }}
          aria-label="Close"
        >
          ✕
        </button>
        <div style={{
          fontFamily: FONT.itemTitle,
          fontStyle: "italic",
          fontSize: 18,
          color: COLOR.ink,
          textAlign: "center",
          flex: 1,
        }}>
          {petName && comboName ? `${petName} & ${comboName}` : (comboName || "Color it!")}
        </div>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="mcm-focusable"
          style={{
            padding: "8px 14px",
            background: COLOR.leaf,
            color: "#fff",
            border: "none",
            borderRadius: 10,
            fontFamily: FONT.mono,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.08em",
            cursor: busy ? "wait" : "pointer",
            opacity: busy ? 0.6 : 1,
            touchAction: "manipulation",
          }}
        >
          {busy ? "…" : "SAVE"}
        </button>
      </div>

      {/* Canvas stack */}
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        padding: 8,
      }}>
        <div style={{
          position: "relative",
          width: "min(90vw, calc(100vh - 200px))",
          aspectRatio: "1 / 1",
          background: "#FFFFFF",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: `0 4px 16px ${COLOR.shadowSoft || "rgba(0,0,0,0.08)"}`,
          touchAction: "none",
        }}>
          <canvas
            ref={fillCanvasRef}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              // touch-action:none lets the native listeners
              // preventDefault and stop iOS from hijacking gestures
              // (scroll, zoom, magnifier on long-press).
              touchAction: "none",
            }}
          />
          <canvas
            ref={lineCanvasRef}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
            }}
          />
        </div>
      </div>

      {/* Saved toast — overlays everything else briefly */}
      {savedToast && (
        <div style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 400,
          padding: "20px 28px",
          background: COLOR.leaf,
          color: "#fff",
          borderRadius: 20,
          fontFamily: FONT.itemTitle,
          fontStyle: "italic",
          fontSize: 22,
          textAlign: "center",
          boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
          pointerEvents: "none",
          animation: "kid-saved-pop 320ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}>
          ✓ Saved to your coloring book!
          <style>{`@keyframes kid-saved-pop {
            from { transform: translate(-50%, -50%) scale(0.6); opacity: 0; }
            to   { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
          }`}</style>
        </div>
      )}

      {/* Palette + tools */}
      <div style={{ padding: "8px 12px 12px" }}>
        {error && (
          <div style={{
            marginBottom: 8,
            padding: "8px 12px",
            background: COLOR.roseDeep,
            border: `1px solid ${COLOR.roseDim}`,
            borderRadius: 8,
            color: COLOR.rose,
            fontFamily: FONT.forma,
            fontSize: 12,
          }}>
            {error}
          </div>
        )}
        <div style={{
          display: "flex",
          gap: 6,
          marginBottom: 10,
          overflowX: "auto",
          paddingBottom: 4,
        }}>
          {PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              aria-label={`color ${c}`}
              style={{
                flex: "0 0 auto",
                width: 40,
                height: 40,
                borderRadius: 20,
                background: c,
                border: color === c ? `3px solid ${COLOR.ink}` : `2px solid ${COLOR.border}`,
                cursor: "pointer",
                touchAction: "manipulation",
              }}
            />
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <ToolButton active={tool === "bucket"} onClick={() => setTool("bucket")} emoji="🪣" label="FILL" />
          <ToolButton active={tool === "brush"}  onClick={() => setTool("brush")}  emoji="🖌️" label="BRUSH" />
          <ToolButton active={tool === "eraser"} onClick={() => setTool("eraser")} emoji="⬜" label="ERASE" />
          <ToolButton active={false}             onClick={undo}                    emoji="↶"  label="UNDO" />
        </div>
      </div>
    </div>
  );
}

function ToolButton({ active, onClick, emoji, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mcm-focusable"
      style={{
        flex: 1,
        padding: "10px 4px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        background: active ? COLOR.leafDeep : "#fff",
        border: `2px solid ${active ? COLOR.leaf : COLOR.border}`,
        borderRadius: 10,
        cursor: "pointer",
        touchAction: "manipulation",
      }}
    >
      <span style={{ fontSize: 22 }}>{emoji}</span>
      <span style={{
        fontFamily: FONT.mono,
        fontSize: 10,
        fontWeight: 700,
        color: active ? "#fff" : COLOR.ink,
        letterSpacing: "0.06em",
      }}>{label}</span>
    </button>
  );
}

function getCanvasPoint(e, canvas) {
  const rect = canvas.getBoundingClientRect();
  const x = ((e.clientX - rect.left) / rect.width) * CANVAS_SIZE;
  const y = ((e.clientY - rect.top)  / rect.height) * CANVAS_SIZE;
  return { x, y };
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return [0, 0, 0];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}
